import React, { useState, useEffect, useRef } from 'react';
import { message, Upload } from 'antd';
import { sendGlobalEvent } from '@/utils/emit';
import { GlobalEventName } from '@/utils/constant';
import { showModal } from '@/components/Modal';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import { UseHandleRemoteUploadReturn } from './types';

interface useHandleRemoteUploadProps {
  config?: FormConfig;
  onFieldChange?: (
    fieldName: string | string[],
    value: any,
    action?: 'add' | 'delete' | string
  ) => void;
  max?: number;
  maxSize?: number;
}

function useHandleRemoteUpload(
  props: useHandleRemoteUploadProps
): UseHandleRemoteUploadReturn {
  const { config, onFieldChange, max = 5, maxSize = 10 * 1024 * 1024 } = props;

  const [fileListMap, setFileListMap] = useState<any>({}); // 文件列表,key为fieldName，value为对应的上传图片list
  const [showUploading, setShowUploading] = useState(false); // 是否显示上传中
  const [uploadKey, setUploadKey] = useState<any>('init'); // 上传key，用于强制刷新Upload组件
  const createdBlobUrlsRef = useRef<Set<string>>(new Set()); // 用于记录已经创建的blobUrl，便于回收

  /**
   * 回显已经有的文件列表
   */
  useEffect(() => {
    config?.data?.forEach((field: FieldItem) => {
      if (
        (field.cmpType == 'upload' || field.cmpType == 'preview') &&
        typeof field.fieldName === 'string'
      ) {
        fileListMap[field.fieldName] = field.defaultFileList || [];
      }
    });
    setFileListMap({
      ...fileListMap,
    });
  }, [config]);

  /**
   * 清理通过createObjectURL创建的所有blobUrl，防止内存泄漏
   */
  useEffect(() => {
    return () => {
      try {
        createdBlobUrlsRef.current.forEach((u) => {
          try {
            if (typeof URL !== 'undefined') {
              URL.revokeObjectURL(u);
            }
          } catch (_) {}
        });
      } catch (_) {}
      createdBlobUrlsRef.current.clear();
    };
  }, []);

  /**
   * 远程上传附件
   * @param {*} option
   * @param {string} fieldName
   * @return {*}
   */
  const remoteUploadCustomRequest = (option: any, fieldName: string) => {
    if (!option?.file) {
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(option.file);
    reader.onloadend = async (e: any) => {
      option.onSuccess();
      const formData = new FormData();
      option.file && formData.append('file', option.file);
      const requestParam: RequestOptions = {
        method: 'POST',
        path: option?.action || '/monitor/web/jira/jira-attachment', // jira上传附件
        contentType: 'formData',
        body: formData,
      };
      const res: any = await request(requestParam);
      if (res.code != HttpStatusCode.Success) {
        message.error(res.message);
      } else {
        console.log('文件上传中');
        onFieldChange &&
          onFieldChange(
            fieldName,
            {
              uid: option.file?.uid,
              type: option.file?.type?.startsWith('video')
                ? 'video'
                : option.file?.type?.startsWith('image')
                  ? 'image'
                  : option.file?.type,
              name: option.file?.name,
              fileKey: res?.data?.fileKey,
            },
            'add'
          );
      }
      sendGlobalEvent(GlobalEventName.JDX_FILE_UPLOAD_STATUS, {
        fieldName: fieldName,
        uploading: false,
      });
      setShowUploading(false);
    };
  };

  /**
   * 上传文件过程中执行
   * 上传过程中，显示上传中
   * 上传完毕，添加文件到列表
   * @param {*} data
   * @param {string} fieldName
   */
  const onUploadChange = (data: any, fieldName: string) => {
    if (data.file.status === 'done') {
      console.log('文件上传完了', fileListMap);
      const url =
        data.file?.originFileObj && typeof URL !== 'undefined'
          ? URL.createObjectURL(data.file.originFileObj)
          : data.file.url || data.file.thumbUrl;

      const newItem = {
        uid: data.file?.uid,
        type: data.file?.type?.startsWith('video')
          ? 'video'
          : data.file?.type?.startsWith('image')
            ? 'image'
            : data.file?.type,
        name: data.file?.name,
        url: url,
      };

      // 记录通过createObjectURL创建的blobUrl，便于后续回收
      try {
        if (typeof url === 'string' && url.startsWith('blob:')) {
          createdBlobUrlsRef.current.add(url);
        }
      } catch (_) {}

      setFileListMap((prev: any) => {
        const next = { ...prev };
        next[fieldName] = [...(next[fieldName] || []), newItem];
        return next;
      });
    } else if (data.file.status === 'uploading') {
      sendGlobalEvent(GlobalEventName.JDX_FILE_UPLOAD_STATUS, {
        fieldName: fieldName,
        uploading: true,
      });
      setShowUploading(true);
    }
  };

  /**
   * 上传文件之前的校验
   * @param {*} file
   * @param {string} fieldName
   * @return {*}
   */
  const onBeforeUpload = (file: any, fieldName: string) => {
    if (fileListMap[fieldName]?.length >= max) {
      message.error(`最多支持上传${max}个文件！`);
      return Upload.LIST_IGNORE;
    } else if (file.size > maxSize!) {
      message.error('文件过大，上传失败！');
      return Upload.LIST_IGNORE;
    }
    return file;
  };

  /**
   * 预览已上传的图片（upload组件的预览函数）
   * @param {*} file
   */
  const onPreviewMedia = (file: any) => {
    const src = file?.url || file?.thumbUrl || '';
    showModal({
      title: '图片预览',
      width: 800,
      footer: null,
      className: 'upload-modal',
      content: <img height={600} src={src} />,
    });
  };

  /**
   * 删除预览图片列表中的图片（previewList组件的删除函数）
   * @param {*} file
   * @param {string} fieldName
   */
  const onDeletePreviewMediasList = (file: any, fieldName: string) => {
    const prevList = fileListMap[fieldName] || [];
    const newList = prevList.filter(
      (item: any) => item.fileKey != file.fileKey || item.uid != file.uid
    );

    const removed = prevList.filter(
      (item: any) => !newList.find((n: any) => n.uid === item.uid)
    );
    removed.forEach((item: any) => {
      try {
        if (
          item?.url &&
          typeof item.url === 'string' &&
          item.url.startsWith('blob:') &&
          typeof URL !== 'undefined'
        ) {
          URL.revokeObjectURL(item.url);
          // 在删除图片时，同时清除ref记录中的blobUrl
          createdBlobUrlsRef.current.delete(item.url);
        }
      } catch (_) {}
    });

    onFieldChange && onFieldChange(fieldName, file, 'delete');
    setFileListMap({
      ...fileListMap,
      [fieldName]: newList,
    });
    setUploadKey(Date.now());
  };

  /**
   * 删除已上传的图片（upload组件的删除函数）
   * @param {*} file
   * @param {string} fieldName
   */
  const onRemoveUploadedMedia = (file: any, fieldName: string) => {
    onFieldChange && onFieldChange(fieldName, file, 'delete');
  };

  return {
    fileListMap,
    showUploading,
    uploadKey,
    remoteUploadCustomRequest,
    onUploadChange,
    onBeforeUpload,
    onPreviewMedia,
    onDeletePreviewMediasList,
    onRemoveUploadedMedia,
  };
}

export default useHandleRemoteUpload;
